import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';

const ProximasClasesAdminWidget = () => {
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);
  const [proximoEvento, setProximoEvento] = useState(null);
  const [eventosDelDia, setEventosDelDia] = useState([]);
  const [countdown, setCountdown] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.rol === 'admin') {
      loadProximosEventos();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Obtener hora actual en Uruguay (UTC-3) de forma consistente
  const getAhoraUruguay = () => {
    const ahoraStr = new Date().toLocaleString('en-US', { timeZone: 'America/Montevideo' });
    return new Date(ahoraStr);
  };

  // Parsear fecha de evento a hora de Uruguay
  const parseFechaUruguay = (fechaStr) => {
    if (!fechaStr) return null;
    const fecha = new Date(fechaStr);
    // Convertir a string de Uruguay y crear nueva fecha
    const uruguayStr = fecha.toLocaleString('en-US', { timeZone: 'America/Montevideo' });
    return new Date(uruguayStr);
  };

  // Actualizar countdown cada segundo
  useEffect(() => {
    if (!proximoEvento?.fecha_inicio) return;

    const interval = setInterval(() => {
      const ahora = getAhoraUruguay();
      const proximaFecha = parseFechaUruguay(proximoEvento.fecha_inicio);
      
      if (!proximaFecha) {
        setCountdown({ dias: 0, horas: 0, minutos: 0, segundos: 0, esHoy: true });
        return;
      }
      
      const diff = proximaFecha - ahora;

      if (diff <= 0) {
        setCountdown({ dias: 0, horas: 0, minutos: 0, segundos: 0, esHoy: true });
      } else {
        const dias = Math.floor(diff / (1000 * 60 * 60 * 24));
        const horas = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const segundos = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown({ dias, horas, minutos, segundos, esHoy: dias === 0 });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [proximoEvento]);

  const loadProximosEventos = async () => {
    try {
      // Obtener próximos eventos del calendario (TODOS, sin filtrar por categoría)
      const response = await axios.get(`${API}/eventos/proximos-widget`);
      const data = response.data;
      
      if (data.success && data.eventos && data.eventos.length > 0) {
        const primerEvento = data.eventos[0];
        setProximoEvento(primerEvento);
        
        // Obtener todos los eventos del mismo día
        const fechaPrimerEvento = new Date(primerEvento.fecha_inicio);
        const mismoDia = data.eventos.filter(evento => {
          const fechaEvento = new Date(evento.fecha_inicio);
          return fechaEvento.toDateString() === fechaPrimerEvento.toDateString();
        });
        
        setEventosDelDia(mismoDia);
      }
    } catch (error) {
      console.error('Error al cargar eventos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Formatear fecha completa
  const formatearFechaCompleta = (fechaStr) => {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr);
    const opciones = { weekday: 'long', day: 'numeric', month: 'long' };
    const resultado = fecha.toLocaleDateString('es-ES', opciones);
    return resultado.charAt(0).toUpperCase() + resultado.slice(1);
  };

  // Formatear hora
  const formatearHora = (fechaStr) => {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr);
    return fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  // Solo mostrar para admin
  if (!user || user.rol !== 'admin') {
    return null;
  }

  // No mostrar si está cargando o no hay eventos
  if (loading || !proximoEvento) {
    return null;
  }

  return (
    <div
      onClick={() => navigate('/app/calendario')}
      className="group relative bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden h-56"
      data-testid="proximas-clases-admin-widget"
    >
      {/* Efecto de brillo animado */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

      {/* Contenido */}
      <div className="relative z-10 h-full flex flex-col p-4 text-white">
        {/* Encabezado con fecha completa */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <i className="fas fa-calendar-day text-lg" />
          </div>
          <div>
            <p className="text-xs opacity-80">Próximo evento</p>
            <p className="text-sm font-bold">{formatearFechaCompleta(proximoEvento.fecha_inicio)}</p>
          </div>
        </div>

        {/* Countdown único */}
        {countdown && (
          <div className="flex justify-center my-2">
            {countdown.esHoy && countdown.horas === 0 && countdown.minutos === 0 ? (
              <div className="text-center">
                <div className="text-3xl font-black">
                  <i className="fas fa-calendar-check mr-2" />
                  ¡HOY!
                </div>
              </div>
            ) : countdown.dias === 0 ? (
              <div className="flex gap-2 text-center">
                <div className="bg-white/20 rounded-lg px-3 py-1 backdrop-blur-sm">
                  <div className="text-xl font-black">{String(countdown.horas).padStart(2, '0')}</div>
                  <div className="text-xs opacity-80">hrs</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-1 backdrop-blur-sm">
                  <div className="text-xl font-black">{String(countdown.minutos).padStart(2, '0')}</div>
                  <div className="text-xs opacity-80">min</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-1 backdrop-blur-sm">
                  <div className="text-xl font-black">{String(countdown.segundos).padStart(2, '0')}</div>
                  <div className="text-xs opacity-80">seg</div>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 text-center">
                <div className="bg-white/20 rounded-lg px-3 py-1 backdrop-blur-sm">
                  <div className="text-xl font-black">{countdown.dias}</div>
                  <div className="text-xs opacity-80">días</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-1 backdrop-blur-sm">
                  <div className="text-xl font-black">{String(countdown.horas).padStart(2, '0')}</div>
                  <div className="text-xs opacity-80">hrs</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-1 backdrop-blur-sm">
                  <div className="text-xl font-black">{String(countdown.minutos).padStart(2, '0')}</div>
                  <div className="text-xs opacity-80">min</div>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-1 backdrop-blur-sm">
                  <div className="text-xl font-black">{String(countdown.segundos).padStart(2, '0')}</div>
                  <div className="text-xs opacity-80">seg</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lista de eventos del día */}
        <div className="flex-1 mt-2">
          <p className="text-xs opacity-80 mb-1">
            <i className="fas fa-list mr-1" />
            Eventos del día ({eventosDelDia.length}):
          </p>
          <div className="space-y-1">
            {eventosDelDia.slice(0, 3).map((evento, index) => (
              <div key={evento.id} className="flex items-center gap-2 text-sm">
                <span className="text-xs opacity-70 w-12">{formatearHora(evento.fecha_inicio)}</span>
                <span className="truncate font-medium">{evento.titulo}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-1 border-t border-white/20 flex items-center justify-between">
          <span className="text-xs opacity-70">
            <i className="fas fa-calendar mr-1" />
            Calendario
          </span>
          <span className="text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
            Ver todo <i className="fas fa-arrow-right ml-1" />
          </span>
        </div>
      </div>

      {/* Decoración */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12" />
      <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/10 rounded-full -ml-10 -mb-10" />
    </div>
  );
};

export default ProximasClasesAdminWidget;
